/**
 * Stripe Context Provider
 *
 * Provides Stripe.js instance and Elements provider to the application.
 * Handles lazy loading of Stripe and configuration fetching.
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useStripeConfig } from '@/hooks/usePayments';

// =============================================================================
// Types
// =============================================================================

interface StripeContextValue {
    stripe: Stripe | null;
    isLoading: boolean;
    error: string | null;
    publishableKey: string | null;
}

interface StripeProviderProps {
    children: ReactNode;
}

// =============================================================================
// Context
// =============================================================================

const StripeContext = createContext<StripeContextValue>({
    stripe: null,
    isLoading: true,
    error: null,
    publishableKey: null,
});

const useStripe = () => {
    const context = useContext(StripeContext);
    if (!context) {
        throw new Error('useStripe must be used within a StripeProvider');
    }
    return context;
};

// =============================================================================
// Provider
// =============================================================================

/**
 * Stripe Provider Component
 *
 * Wraps your app to provide Stripe functionality.
 * Automatically fetches the publishable key from the API and initializes Stripe.
 *
 * Usage:
 * ```tsx
 * <StripeProvider>
 *   <App />
 * </StripeProvider>
 * ```
 */
export const StripeProvider = ({ children }: StripeProviderProps) => {
    const { data: config, isLoading: configLoading, error: configError } = useStripeConfig();
    const [stripe, setStripe] = useState<Stripe | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const initStripe = async () => {
            if (!config?.publishableKey) return;

            try {
                setIsLoading(true);
                const stripeInstance = await loadStripe(config.publishableKey);
                setStripe(stripeInstance);
                setError(null);
            } catch (err) {
                setError('Failed to initialize payment system');
                console.error('Stripe initialization error:', err);
            } finally {
                setIsLoading(false);
            }
        };

        if (config?.publishableKey) {
            initStripe();
        }
    }, [config?.publishableKey]);

    useEffect(() => {
        if (configError) {
            setError('Failed to load payment configuration');
            setIsLoading(false);
        }
    }, [configError]);

    const value: StripeContextValue = {
        stripe,
        isLoading: configLoading || isLoading,
        error,
        publishableKey: config?.publishableKey || null,
    };

    return (
        <StripeContext.Provider value={value}>
            {children}
        </StripeContext.Provider>
    );
};

// =============================================================================
// Elements Wrapper
// =============================================================================

interface StripeElementsWrapperProps {
    clientSecret: string;
    children: ReactNode;
    publishableKey?: string;
    appearance?: {
        theme?: 'stripe' | 'night' | 'flat';
        variables?: Record<string, string>;
    };
}

const STRIPE_APPEARANCE_DEFAULTS = {
    colorPrimary: '#0F766E',
    colorBackground: '#FFFFFF',
    colorText: '#374151',
    colorTextSecondary: '#68606f',
    colorDanger: '#D9534F',
    colorBorder: '#e0ddd2',
    fontFamily: 'Nunito, ui-sans-serif, system-ui, sans-serif',
    spacingUnit: '4px',
    borderRadius: '10px',
};

const getStripeAppearanceVariables = () => {
    if (typeof window === 'undefined') {
        return STRIPE_APPEARANCE_DEFAULTS;
    }

    const styles = window.getComputedStyle(document.documentElement);

    const readToken = (name: string, fallback: string) => {
        const value = styles.getPropertyValue(name).trim();
        return value ? `hsl(${value})` : fallback;
    };

    return {
        colorPrimary: readToken('--primary', STRIPE_APPEARANCE_DEFAULTS.colorPrimary),
        colorBackground: readToken('--card', STRIPE_APPEARANCE_DEFAULTS.colorBackground),
        colorText: readToken('--foreground', STRIPE_APPEARANCE_DEFAULTS.colorText),
        colorTextSecondary: readToken('--muted-foreground', STRIPE_APPEARANCE_DEFAULTS.colorTextSecondary),
        colorDanger: readToken('--destructive', STRIPE_APPEARANCE_DEFAULTS.colorDanger),
        colorBorder: readToken('--border', STRIPE_APPEARANCE_DEFAULTS.colorBorder),
        fontFamily: STRIPE_APPEARANCE_DEFAULTS.fontFamily,
        spacingUnit: STRIPE_APPEARANCE_DEFAULTS.spacingUnit,
        borderRadius: STRIPE_APPEARANCE_DEFAULTS.borderRadius,
    };
};

/**
 * Wrapper component that provides Stripe Elements with a client secret.
 * Use this to wrap payment forms that need access to Stripe Elements.
 *
 * Usage:
 * ```tsx
 * <StripeElementsWrapper clientSecret={clientSecret}>
 *   <PaymentForm />
 * </StripeElementsWrapper>
 * ```
 */
export const StripeElementsWrapper = ({
    clientSecret,
    children,
    publishableKey,
    appearance,
}: StripeElementsWrapperProps) => {
    const { stripe, isLoading, error, publishableKey: defaultPublishableKey } = useStripe();
    const [overrideStripe, setOverrideStripe] = useState<Stripe | null>(null);
    const [overrideLoading, setOverrideLoading] = useState(false);
    const [overrideError, setOverrideError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        const overrideKey = publishableKey;

        const initOverrideStripe = async () => {
            if (!overrideKey || overrideKey === defaultPublishableKey) {
                if (isMounted) {
                    setOverrideStripe(null);
                    setOverrideError(null);
                    setOverrideLoading(false);
                }
                return;
            }

            try {
                if (isMounted) {
                    setOverrideLoading(true);
                    setOverrideError(null);
                }

                const stripeInstance = await loadStripe(overrideKey);

                if (isMounted) {
                    setOverrideStripe(stripeInstance);
                }
            } catch (err) {
                if (isMounted) {
                    setOverrideError('Failed to initialize payment system');
                    console.error('Stripe override initialization error:', err);
                }
            } finally {
                if (isMounted) {
                    setOverrideLoading(false);
                }
            }
        };

        initOverrideStripe();

        return () => {
            isMounted = false;
        };
    }, [defaultPublishableKey, publishableKey]);

    const effectiveStripe = publishableKey && publishableKey !== defaultPublishableKey
        ? overrideStripe
        : stripe;
    const effectiveLoading = (publishableKey && publishableKey !== defaultPublishableKey)
        ? overrideLoading
        : isLoading;
    const effectiveError = (publishableKey && publishableKey !== defaultPublishableKey)
        ? overrideError
        : error;

    if (effectiveLoading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (effectiveError || !effectiveStripe) {
        return (
            <div className="p-4 bg-destructive-subtle border border-destructive-border rounded-lg text-destructive">
                {effectiveError || 'Payment system unavailable. Please try again later.'}
            </div>
        );
    }

    const options = {
        clientSecret,
        appearance: {
            theme: appearance?.theme || 'stripe' as const,
            variables: {
                ...getStripeAppearanceVariables(),
                ...appearance?.variables,
            },
        },
    };

    return (
        <Elements stripe={effectiveStripe} options={options}>
            {children}
        </Elements>
    );
};

export default StripeProvider;
